import { Component } from '@theme/component';

export const WISHLIST_STORAGE_KEY = 'theme-wishlist';
export const WISHLIST_CHANGE_EVENT = 'theme:wishlist-change';

/**
 * @typedef {{ id: string, handle: string }} WishlistItem
 */

/**
 * Reads saved products and upgrades the previous ID-only format in memory.
 * @returns {WishlistItem[]}
 */
export function readWishlist() {
  const value = JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY) || '[]');

  if (!Array.isArray(value)) {
    throw new TypeError('Saved wishlist data is not a list.');
  }

  return value.map((item) => {
    if (typeof item === 'string') {
      return { id: item, handle: '' };
    }

    if (
      item &&
      typeof item === 'object' &&
      typeof item.id === 'string' &&
      typeof item.handle === 'string'
    ) {
      return { id: item.id, handle: item.handle };
    }

    throw new TypeError('Saved wishlist item has an invalid format.');
  });
}

/**
 * @param {WishlistItem[]} items
 */
export function writeWishlist(items) {
  localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent(WISHLIST_CHANGE_EVENT));
}

/**
 * @param {WishlistItem[]} items
 */
function updateWishlistUI(items) {
  const count = String(items.length);
  document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    element.textContent = count;
    element.hidden = items.length === 0;
  });

  document.querySelectorAll('wishlist-button').forEach((wishlistButton) => {
    if (!(wishlistButton instanceof HTMLElement)) return;
    const button = wishlistButton.querySelector('button');
    if (!button) return;

    const id = wishlistButton.dataset.productId ?? '';
    const handle = wishlistButton.dataset.productHandle ?? '';
    const saved = items.some((item) => item.id === id || (handle && item.handle === handle));
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute(
      'aria-label',
      saved ? wishlistButton.dataset.removeLabel ?? '' : wishlistButton.dataset.addLabel ?? ''
    );
  });
}

window.addEventListener('storage', (event) => {
  if (event.key !== WISHLIST_STORAGE_KEY) return;

  try {
    updateWishlistUI(readWishlist());
  } catch (error) {
    console.error('[wishlist] Could not read saved favorites after a storage update:', error);
  }
});

window.addEventListener(WISHLIST_CHANGE_EVENT, () => {
  try {
    updateWishlistUI(readWishlist());
  } catch (error) {
    console.error('[wishlist] Could not update the saved favorites display:', error);
  }
});

try {
  updateWishlistUI(readWishlist());
} catch (error) {
  console.error('[wishlist] Could not initialize the saved favorites display:', error);
}

/**
 * Heart button on product cards.
 *
 * @extends {Component}
 */
class WishlistButton extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const button = this.querySelector('button');
    if (!button) return;

    try {
      const items = readWishlist();
      const id = this.dataset.productId ?? '';
      const handle = this.dataset.productHandle ?? '';
      const legacyIndex = items.findIndex((item) => item.id === id && !item.handle);

      if (legacyIndex !== -1 && handle) {
        items[legacyIndex] = { id, handle };
        writeWishlist(items);
      } else {
        updateWishlistUI(items);
      }
    } catch (error) {
      console.error('[wishlist] Could not initialize the favorite button:', error);
    }

    button.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        try {
          const id = this.dataset.productId ?? '';
          const handle = this.dataset.productHandle ?? '';
          const items = readWishlist();
          const isSaved = items.some((item) => item.id === id || (handle && item.handle === handle));
          const nextItems = isSaved
            ? items.filter((item) => item.id !== id && (!handle || item.handle !== handle))
            : [...items, { id, handle }];

          writeWishlist(nextItems);
        } catch (error) {
          console.error('[wishlist] Could not save this favorite:', error);
        }
      },
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    super.disconnectedCallback();
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
